import React from "react";
import { Route, Navigate } from "react-router-dom";
import Dashboard from "../components/business/dashboards";
import { getLoginUserData, isAuthenticated } from "../services/account.service";
import BusinessProfile from "../components/business/profile/businessProfile";
import CoOwners from "../components/business/co-owner";
import { ROLES } from "../constants/general.constant";
import NotFoundPage from "../components/errors/404";
import Loader from "../common/loader";
import Employees from "../components/co-owner/employees";
import Customers from "../components/common/customerDetails";
import CustomerProfile from "../components/common/customerProfile/customerProfile";
import Leads from "../components/common/leads";
import Bankers from "../components/common/bankerDetails";
import MainLayout from "../layout/mainLayout";
import DealsPage from "../components/customers/deals";
import InactiveDealsPage from "../components/customers/inactiveDeals";
import SocialPosts from "../components/business/socialPosts";
import Notifications from "../components/customers/notifications";
import TaskManager from "../components/business/tasks/TaskManager";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRole = ROLES.BUSINESS,
}) => {
  const [role, setRole] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const user = await getLoginUserData();
        if (user?.role) {
          setRole(user.role);
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserRole();
  }, []);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (loading) return <Loader />;

  if (role !== allowedRole) return <NotFoundPage />;

  return <>{children}</>;
};

// 🔹 Wrappers
const WithLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRole={ROLES.BUSINESS}>
    <MainLayout>{children}</MainLayout>
  </ProtectedRoute>
);

const WithoutLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRole={ROLES.BUSINESS}>{children}</ProtectedRoute>
);

// 🔹 Route config
const businessRoutesConfig = [
  { key: "home", path: "/", element: <Dashboard />, layout: "with" },
  { key: "dashboard", path: "/dashboard", element: <Dashboard />, layout: "with" },
  { key: "business-co-owners", path: "/business-co-owners", element: <CoOwners />, layout: "with" },
  { key: "business-profile", path: "/business-profile", element: <BusinessProfile />, layout: "with" },
  { key: "employees", path: "/employees", element: <Employees />, layout: "with" },
  { key: "customers", path: "/customers", element: <Customers />, layout: "with" },
  { key: "customer-profile", path: "/customers/:customerId", element: <CustomerProfile />, layout: "without" },
  { key: "leads", path: "/leads", element: <Leads />, layout: "with" },
  { key: "bankers", path: "/bankers", element: <Bankers />, layout: "with" },
  { key: "notifications", path: "/notifications", element: <Notifications />, layout: "with" },
  { key: "tasks", path: "/tasks", element: <TaskManager />, layout: "with" },
  { key: "deals", path: "/deals", element: <DealsPage />, layout: "with" },
  { key: "inactive-deals", path: "/inactive-deals", element: <InactiveDealsPage />, layout: "with" },
  { key: "social-posts", path: "/social-posts", element: <SocialPosts />, layout: "with" },
];

// 🔹 Build dynamic routes
const BusinessRoutes = businessRoutesConfig.map(({ key, path, element, layout }) => (
  <Route
    key={key}
    path={path}
    element={layout === "with" ? <WithLayout>{element}</WithLayout> : <WithoutLayout>{element}</WithoutLayout>}
  />
));

export default BusinessRoutes;
